import React, { useContext, useMemo, useEffect } from 'react';

import { useToken } from './useToken';
import { PermissionsProvider } from '../Permissions';
import { useSetPermissions } from './useSetPermissions';
import type { LoginRequest } from '../../api';

export type UseAuthResult = {
  user?: UserType | null;

  isSuper: boolean;
  isLoading: boolean;
  isAuthorized: boolean;
  isAuthenticated: boolean;

  permissions: PermissionType[];

  check: () => void;
  logout: () => void;
  startLogin: () => void;
  login: (payload: LoginRequest) => Promise<string | null>;
};

const UserContext = React.createContext<UseAuthResult>({
  isSuper: false,
  isLoading: false,
  isAuthorized: false,
  isAuthenticated: false,

  permissions: [],

  check: async () => {},
  login: async () => {
    return null;
  },
  logout: () => {
    console.log('logout: no user context');
  },
  startLogin: () => {
    console.log('loginWithRedirect: no user context');
  },
});

type UserProviderProps = {
  children: React.ReactNode;
};

const AutoLoginRedirect = () => {
  const { startLogin } = useAuth();

  useEffect(() => {
    startLogin();
  }, [startLogin]);

  return null;
};

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const tokenValue = useToken();
  const userValue = useSetPermissions(
    tokenValue.isLoading ?? true,
    tokenValue.startLogin ?? (() => void 0)
  );

  const value = useMemo(() => {
    const isSuper = !!tokenValue?.user?.app_metadata?.superAdmin;
    return {
      ...tokenValue,
      ...userValue,
      isSuper: isSuper,
      isAuthenticated: tokenValue.isAuthenticated ?? false,
      isLoading: tokenValue.isLoading || !userValue.inited,
      isAuthorized: isSuper || !!userValue.permissions.length,

      check: tokenValue.check || (() => void 0),
      logout: tokenValue.logout || (() => void 0),
      login: tokenValue.login || (async () => null),
      startLogin: tokenValue.startLogin || (() => void 0),
    };
  }, [tokenValue, userValue]);

  if (value.isLoading) return null;

  return (
    <UserContext.Provider value={value}>
      {value.isAuthenticated ? (
        <PermissionsProvider
          permissions={value.permissions.map(p => ({
            action: p,
            allowed: true,
          }))}
        >
          {children}
        </PermissionsProvider>
      ) : (
        <AutoLoginRedirect />
      )}
    </UserContext.Provider>
  );
};

export const useAuth = () => useContext(UserContext);
export { useUserSettings } from './useUserSettings';
